import { supabase } from "@/lib/supabase/supabaseClient";
import { store } from "@/lib/states/store";

const apiOrigin = () =>
  (import.meta.env.VITE_API_URL ?? "http://127.0.0.1:8790").replace(/\/$/, "");

export class WorkoutTranscribeError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "WorkoutTranscribeError";
  }
}

const fileExtension = (type: string) =>
  type.includes("mp4") || type.includes("m4a") || type.includes("aac") ? "m4a" : "webm";

export async function transcribeWorkoutAudio(
  blob: Blob,
  signal?: AbortSignal,
): Promise<string> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session) {
    throw new WorkoutTranscribeError("Not authenticated");
  }

  const orgId = store.getState().session.org?.id;
  if (!orgId) {
    throw new WorkoutTranscribeError("No organisation selected");
  }

  const form = new FormData();
  form.append("audio", blob, `instruction.${fileExtension(blob.type)}`);

  let response: Response;
  try {
    response = await fetch(`${apiOrigin()}/ai/workout-transcribe`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        "x-org-id": orgId,
      },
      body: form,
      signal,
    });
  } catch (error) {
    if (error instanceof Error && error.name === "AbortError") throw error;
    throw new WorkoutTranscribeError(
      error instanceof Error ? error.message : "Network error",
    );
  }

  if (!response.ok) {
    let message = `Request failed (${response.status})`;
    try {
      const body = await response.json();
      if (typeof body.error === "string") message = body.error;
    } catch {
      // ignore
    }
    throw new WorkoutTranscribeError(message);
  }

  const payload = (await response.json().catch(() => null)) as { text?: unknown } | null;
  return typeof payload?.text === "string" ? payload.text.trim() : "";
}
